import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  Calendar, 
  CalendarX,
  CalendarCheck,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';

const ArtistAttendance = () => {
  const { isDark } = useTheme();
  const [attendance, setAttendance] = useState({ unplanned_leaves: [], total_unplanned: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/attendance');
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  // Group leaves by year and month
  const groupedLeaves = attendance.unplanned_leaves.reduce((acc, leave) => {
    const key = `${leave.year}-${leave.month}`;
    if (!acc[key]) {
      acc[key] = {
        year: leave.year,
        month: leave.month,
        leaves: []
      };
    }
    acc[key].leaves.push(leave);
    return acc;
  }, {});

  const sortedGroups = Object.values(groupedLeaves).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-attendance">
        {/* Header */}
        <div>
          <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            <Calendar className="w-7 h-7 text-[#331062]" />
            Attendance Summary
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Track your unplanned leave history
          </p>
        </div>

        {/* Summary Card */}
        <Card className={cn(
          "p-6",
          attendance.total_unplanned > 5 
            ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800" 
            : (isDark ? "bg-slate-800 border-slate-700" : "")
        )}>
          <div className="flex items-center gap-4">
            <div className={cn(
              "p-4 rounded-xl",
              attendance.total_unplanned > 5 
                ? "bg-red-100 dark:bg-red-900/50" 
                : "bg-yellow-100 dark:bg-yellow-900/50"
            )}>
              {attendance.total_unplanned > 5 ? (
                <CalendarX className="w-10 h-10 text-red-600 dark:text-red-400" />
              ) : (
                <CalendarCheck className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
              )}
            </div>
            <div>
              <p className={cn(
                "text-4xl font-bold",
                attendance.total_unplanned > 5 ? "text-red-600" : "text-yellow-600"
              )}>
                {attendance.total_unplanned}
              </p>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                Total Unplanned Leaves
              </p>
            </div>
            {attendance.total_unplanned > 5 && (
              <div className="ml-auto flex items-start gap-2 p-3 bg-red-100 dark:bg-red-900/50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">
                  High number of unplanned leaves. Please plan better.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Leave History */}
        <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            <TrendingDown className="w-5 h-5 text-[#331062]" />
            Unplanned Leave History
          </h3>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
            </div>
          ) : attendance.total_unplanned === 0 ? (
            <div className="text-center py-12">
              <CalendarCheck className={cn("w-16 h-16 mx-auto mb-4 text-green-500")} />
              <h4 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
                Excellent Attendance! 🎉
              </h4>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                You have no unplanned leaves recorded. Keep it up!
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedGroups.map((group, groupIndex) => (
                <motion.div
                  key={`${group.year}-${group.month}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.1 }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                    <h4 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                      {getMonthName(group.month)} {group.year}
                    </h4>
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full",
                      group.leaves.length >= 3 
                        ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                        : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300"
                    )}>
                      {group.leaves.length} leave{group.leaves.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                    {group.leaves.map((leave, index) => (
                      <div
                        key={index}
                        className={cn(
                          "p-3 rounded-lg text-center",
                          isDark ? "bg-slate-700" : "bg-slate-100"
                        )}
                      >
                        <CalendarX className="w-5 h-5 mx-auto text-red-500 mb-1" />
                        <p className={cn("text-xs font-medium", isDark ? "text-white" : "text-slate-700")}>
                          Week {leave.week}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </Card>

        {/* Tips Card */}
        <Card className={cn("p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20", isDark ? "border-slate-700" : "border-blue-100")}>
          <h4 className={cn("font-semibold mb-3 flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            💡 Attendance Tips
          </h4>
          <ul className={cn("space-y-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
            <li>• Plan your leaves in advance to avoid unplanned absences</li>
            <li>• Communicate with your manager about any health or personal issues</li>
            <li>• Use planned leave for known appointments or events</li>
            <li>• Maintain good work-life balance to stay healthy and productive</li>
          </ul>
        </Card>
      </div>
    </ArtistLayout>
  );
};

export default ArtistAttendance;
